/** Тексты экрана задания 6.1d (handoff 5 §4). В handoff формулировки —
 *  заглушки («придут отдельным handoff по заданиям»), поэтому здесь
 *  шаблоны по метрике/периоду: заменить централизованно, когда приедут
 *  финальные тексты. {N} — target. */

export interface QuestStep {
  title: string;
  text: string;
}
export interface QuestCondition {
  ok: boolean;
  text: string;
}

interface Tpl {
  about: string;
  step2: QuestStep;
  conditions: QuestCondition[];
}

const n = (tpl: string, target: number) => tpl.replaceAll('{N}', String(target));

const METRIC_TPL: Record<string, Tpl> = {
  knockouts: {
    about:
      'Выбей {N} соперников. Нокаут засчитывается, когда последние фишки игрока уходят тебе — в любой момент турнира.',
    step2: { title: 'Сделай нокаут', text: 'Забери последние фишки соперника за столом' },
    conditions: [
      { ok: true, text: 'Считается любой нокаут в турнире' },
      { ok: true, text: 'Ре-энтри соперника не отменяет нокаут' },
      { ok: false, text: 'Сплит-поты без вылета не считаются' },
    ],
  },
  place_top_half: {
    about:
      'Финишируй не ниже середины поля. Порог считается от числа игроков в итогах: в поле на 24 — это 12-е место или выше.',
    step2: { title: 'Доиграй до верхней половины', text: 'Переживи половину поля — место по итогам' },
    conditions: [
      { ok: true, text: 'Порог — половина поля, округление вниз' },
      { ok: true, text: 'Число игроков берётся из итогов игры' },
      { ok: false, text: 'Ре-энтри не добавляют мест в поле' },
    ],
  },
  place_top_third: {
    about:
      'Финишируй в верхней трети поля. Порог считается от числа игроков в итогах: в поле на 24 — это 8-е место или выше.',
    step2: { title: 'Дойди до верхней трети', text: 'Место по итогам — в первой трети поля' },
    conditions: [
      { ok: true, text: 'Порог — треть поля, округление вниз' },
      { ok: true, text: 'Число игроков берётся из итогов игры' },
      { ok: false, text: 'Ре-энтри не добавляют мест в поле' },
    ],
  },
  ko_and_top_half: {
    about:
      'Двойная цель: сделай хотя бы один нокаут и финишируй не ниже середины поля — всё за одну игру.',
    step2: { title: 'Нокаут + верхняя половина', text: 'Выбей соперника и удержись выше середины поля' },
    conditions: [
      { ok: true, text: 'Оба условия — в одной игре' },
      { ok: true, text: 'Любой нокаут в турнире подходит' },
      { ok: false, text: 'Нокаут без места в верхней половине не считается' },
    ],
  },
  final_table: {
    about: 'Дойди до финального стола — последнего стола турнира, где решается победитель.',
    step2: { title: 'Дойди до финалки', text: 'Останься в игре до последнего стола' },
    conditions: [
      { ok: true, text: 'Финальный стол отмечается в итогах игры' },
      { ok: false, text: 'Вылет на пузыре финалки не считается' },
    ],
  },
  top5: {
    about: 'Финишируй в топ-5 игры — пятое место или выше по итогам.',
    step2: { title: 'Займи топ-5', text: 'Место по итогам — 5-е или выше' },
    conditions: [
      { ok: true, text: 'Считается место из итогов игры' },
      { ok: false, text: 'Шестое место — увы, мимо' },
    ],
  },
  prize: {
    about: 'Займи призовое место — войди в тройку по итогам игры.',
    step2: { title: 'Войди в призы', text: 'Финишируй в тройке лучших' },
    conditions: [
      { ok: true, text: 'Считается место из итогов игры' },
      { ok: false, text: 'Четвёртое место не считается' },
    ],
  },
  play: {
    about:
      'Сыграй {N} вечера на этой неделе. Неделя идёт с понедельника по воскресенье по Москве, игра считается по дате старта.',
    step2: { title: 'Приди и сыграй', text: 'Каждый сыгранный вечер добавляет +1 к прогрессу' },
    conditions: [
      { ok: true, text: 'Считается каждый вечер с результатом в итогах' },
      { ok: false, text: 'Запись без явки не считается' },
    ],
  },
};

const FALLBACK: Tpl = {
  about: 'Выполни условие задания за игру — прогресс засчитается по итогам.',
  step2: { title: 'Выполни условие', text: 'Сыграй так, как требует задание' },
  conditions: [{ ok: true, text: 'Прогресс считается по итогам игры' }],
};

export function questAbout(metric: string, target: number): string {
  return n((METRIC_TPL[metric] ?? FALLBACK).about, target);
}

export function questSteps(
  metric: string,
  target: number,
  opts: { registered: boolean; done: boolean },
): (QuestStep & { done: boolean })[] {
  const t = METRIC_TPL[metric] ?? FALLBACK;
  return [
    {
      title: 'Запишись на турнир',
      text: 'Открой раздел игр и запишись на ближайший вечер',
      done: opts.registered || opts.done,
    },
    { title: n(t.step2.title, target), text: n(t.step2.text, target), done: opts.done },
    {
      title: 'Дождись результатов',
      text: 'Прогресс засчитается после подведения итогов игры',
      done: opts.done,
    },
  ];
}

export function questConditions(metric: string, target: number): QuestCondition[] {
  const t = METRIC_TPL[metric] ?? FALLBACK;
  return [
    ...t.conditions.map((c) => ({ ok: c.ok, text: n(c.text, target) })),
    { ok: false, text: 'Заменить задание нельзя' },
  ];
}
